/**
 * LocationService - P2P location sharing service
 * Handles geolocation, permissions, and real-time location updates
 */

import { EventEmitter } from './EventEmitter'

export interface LocationData {
  latitude: number
  longitude: number
  accuracy: number
  altitude: number | null
  altitudeAccuracy: number | null
  heading: number | null
  speed: number | null
  timestamp: number
}

export interface LocationSharingSession {
  peerId: string
  type: 'one-time' | 'live' | 'duration'
  startTime: number
  endTime?: number
  updateInterval?: number
  watchId?: number
}

export interface LocationPermissions {
  [peerId: string]: {
    canRequest: boolean
    canReceive: boolean
    autoApprove: boolean
    maxAccuracy: 'precise' | 'approximate' | 'city'
  }
}

export class LocationService extends EventEmitter {
  private currentPosition: LocationData | null = null
  private watchId: number | null = null
  private sharingSessions: Map<string, LocationSharingSession> = new Map()
  private permissions: LocationPermissions = {}
  private locationHistory: LocationData[] = []
  private isTracking = false

  // Configuration
  private readonly HIGH_ACCURACY = true
  private readonly TIMEOUT = 10000 // 10 seconds
  private readonly MAX_AGE = 30000 // 30 seconds cache
  private readonly UPDATE_INTERVAL = 5000 // 5 seconds for live tracking
  private readonly MAX_HISTORY = 100
  private readonly ACCURACY_LEVELS = {
    precise: 1, // No modification
    approximate: 100, // Round to ~100m
    city: 1000 // Round to ~1km
  }

  constructor() {
    super()
  }

  /**
   * Check if geolocation permission is granted
   */
  async checkPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unsupported'> {
    if (!navigator.geolocation) {
      return 'unsupported'
    }

    // Check if the browser supports the permissions API
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        return permission.state
      } catch (error) {
        console.warn('Permission API not available, falling back to geolocation test')
      }
    }

    // Fallback: attempt a quick position request to test permissions
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve('granted'),
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            resolve('denied')
          } else {
            resolve('prompt') // Assume prompt needed if other error
          }
        },
        { timeout: 1000, enableHighAccuracy: false, maximumAge: Infinity }
      )
    })
  }

  /**
   * Request permission and get current location
   */
  async getCurrentLocation(): Promise<LocationData> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = this.convertToLocationData(position)
          this.currentPosition = locationData
          this.addToHistory(locationData)
          this.emit('location-updated', locationData)
          resolve(locationData)
        },
        (error) => {
          this.handleLocationError(error)
          reject(error)
        },
        {
          enableHighAccuracy: this.HIGH_ACCURACY,
          timeout: this.TIMEOUT,
          maximumAge: this.MAX_AGE
        }
      )
    })
  }

  /**
   * Start live location tracking
   */
  startLiveTracking(peerId: string, updateInterval = this.UPDATE_INTERVAL): void {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported')
    }

    // Stop any existing tracking for this peer
    this.stopLiveTracking(peerId)

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const locationData = this.convertToLocationData(position)
        this.currentPosition = locationData
        this.addToHistory(locationData)
        this.emit('live-location-update', { peerId, location: locationData })
      },
      (error) => {
        this.handleLocationError(error)
        this.emit('location-error', { peerId, error })
      },
      {
        enableHighAccuracy: this.HIGH_ACCURACY,
        timeout: this.TIMEOUT,
        maximumAge: updateInterval
      }
    )

    this.sharingSessions.set(peerId, {
      peerId,
      type: 'live',
      startTime: Date.now(),
      updateInterval,
      watchId
    })

    this.isTracking = true
  }

  /**
   * Stop live location tracking for a peer
   */
  stopLiveTracking(peerId: string): void {
    const session = this.sharingSessions.get(peerId)
    if (session && session.watchId !== undefined) {
      navigator.geolocation.clearWatch(session.watchId)
      this.sharingSessions.delete(peerId)
      this.emit('tracking-stopped', { peerId })
    }

    // Check if any sessions remain
    if (this.sharingSessions.size === 0) {
      this.isTracking = false
    }
  }

  /**
   * Start duration-based location sharing
   */
  startDurationSharing(peerId: string, durationMinutes: number): void {
    const endTime = Date.now() + (durationMinutes * 60 * 1000)

    this.startLiveTracking(peerId)

    const session = this.sharingSessions.get(peerId)
    if (session) {
      session.type = 'duration'
      session.endTime = endTime
    }

    // Set timeout to stop sharing
    setTimeout(() => {
      this.stopLiveTracking(peerId)
      this.emit('duration-sharing-ended', { peerId })
    }, durationMinutes * 60 * 1000)
  }

  /**
   * Apply privacy level to location data
   */
  applyPrivacyLevel(location: LocationData, accuracyLevel: 'precise' | 'approximate' | 'city'): LocationData {
    const factor = this.ACCURACY_LEVELS[accuracyLevel]

    if (factor === 1) {
      return location // Precise, no modification
    }

    // Round coordinates to reduce precision
    const roundedLat = Math.round(location.latitude * (10000 / factor)) / (10000 / factor)
    const roundedLng = Math.round(location.longitude * (10000 / factor)) / (10000 / factor)

    return {
      ...location,
      latitude: roundedLat,
      longitude: roundedLng,
      accuracy: Math.max(location.accuracy, factor)
    }
  }

  /**
   * Set permission for a peer
   */
  setPermission(peerId: string, permission: Partial<LocationPermissions[string]>): void {
    this.permissions[peerId] = {
      canRequest: permission.canRequest ?? true,
      canReceive: permission.canReceive ?? true,
      autoApprove: permission.autoApprove ?? false,
      maxAccuracy: permission.maxAccuracy ?? 'approximate'
    }
    this.emit('permission-updated', { peerId, permission: this.permissions[peerId] })
  }

  /**
   * Check if peer has permission to request location
   */
  canRequestLocation(peerId: string): boolean {
    return this.permissions[peerId]?.canRequest ?? true
  }

  /**
   * Check if location should be auto-approved for peer
   */
  shouldAutoApprove(peerId: string): boolean {
    return this.permissions[peerId]?.autoApprove ?? false
  }

  /**
   * Get accuracy level for peer
   */
  getAccuracyLevelForPeer(peerId: string): 'precise' | 'approximate' | 'city' {
    return this.permissions[peerId]?.maxAccuracy ?? 'approximate'
  }

  /**
   * Calculate distance between two coordinates (in meters)
   */
  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3 // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180
    const φ2 = lat2 * Math.PI / 180
    const Δφ = (lat2 - lat1) * Math.PI / 180
    const Δλ = (lon2 - lon1) * Math.PI / 180

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c
  }

  /**
   * Format distance for display
   */
  formatDistance(meters: number): string {
    if (meters < 1000) {
      return `${Math.round(meters)}m`
    } else {
      return `${(meters / 1000).toFixed(1)}km`
    }
  }

  /**
   * Convert GeolocationPosition to LocationData
   */
  private convertToLocationData(position: GeolocationPosition): LocationData {
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
      timestamp: position.timestamp
    }
  }

  /**
   * Handle location errors
   */
  private handleLocationError(error: GeolocationPositionError): void {
    let errorMessage = 'Unknown location error'

    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location permission denied'
        break
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location information unavailable'
        break
      case error.TIMEOUT:
        errorMessage = 'Location request timed out'
        break
    }

    this.emit('error', { message: errorMessage, code: error.code })
  }

  /**
   * Add location to history
   */
  private addToHistory(location: LocationData): void {
    this.locationHistory.push(location)
    if (this.locationHistory.length > this.MAX_HISTORY) {
      this.locationHistory.shift()
    }
  }

  /**
   * Get location history
   */
  getLocationHistory(): LocationData[] {
    return [...this.locationHistory]
  }

  /**
   * Clear location history
   */
  clearHistory(): void {
    this.locationHistory = []
  }

  /**
   * Get current sharing sessions
   */
  getActiveSessions(): LocationSharingSession[] {
    return Array.from(this.sharingSessions.values())
  }

  /**
   * Check if currently sharing with a peer
   */
  isSharingWithPeer(peerId: string): boolean {
    return this.sharingSessions.has(peerId)
  }

  /**
   * Stop all location sharing
   */
  stopAllSharing(): void {
    for (const peerId of this.sharingSessions.keys()) {
      this.stopLiveTracking(peerId)
    }
  }

  /**
   * Get last known position
   */
  getLastPosition(): LocationData | null {
    return this.currentPosition
  }

  /**
   * Check if location tracking is active
   */
  isTrackingActive(): boolean {
    return this.isTracking
  }
}

export const locationService = new LocationService()